import { FunctionComponent, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Link from 'antd/es/typography/Link';
import { useCart } from '@hub/api/queries';
import {
  getCheckoutContextState,
  trackMixpanel,
} from '@monorepo/client-common';
import {
  ButtonText,
  PagePath,
  VerificationButtonTypes,
} from '@monorepo/client-types';
import { Button, ButtonProps, Flex, Tooltip } from '@monorepo/react-components';
import {
  AnalyticsEventTypes,
  EventTypes,
  FeatureFlagNames,
  ThemeColors,
} from '@monorepo/types';
import { useStore } from '../../helpers/use-store';
import { useFeatureFlags } from '../../hooks/';
import gtmService from '../../services/analytics/analytics';
import { ThemeWrapper } from '../theme-wrapper/theme-wrapper';

type VerificationButtonProps = ButtonProps & {
  themeColor?: ThemeColors;
  placement?: VerificationButtonTypes;
  id?: string;
  dataTestId?: string;
};
const VerificationRequiredButton: FunctionComponent<
  VerificationButtonProps
> = ({
  children,
  disabled,
  placement,
  themeColor = ThemeColors.colorAttention,
  id,
  dataTestId,
  ...rest
}) => {
  const {
    dataStore: { applicationConfigStore, userStore },
  } = useStore();
  const { t } = useTranslation(['common', 'orders']);
  const { isFeatureEnabled } = useFeatureFlags();
  const navigate = useNavigate();
  const { data: cart } = useCart();
  const paymentsDisabled =
    !isFeatureEnabled(FeatureFlagNames.DisablePayments) ||
    applicationConfigStore.applicationConfig.isBlacklisted;

  const shouldDisableButton =
    disabled || userStore.verificationRequired || paymentsDisabled;

  const getTooltipText = () => {
    if (userStore.verificationRequired) {
      return (
        <Flex>
          <span>
            {t('orders:verification_required')}:{' '}
            <Link onClick={() => navigate('/profile/verification')}>
              {t('common:click_here')}
            </Link>
          </span>
        </Flex>
      );
    } else if (paymentsDisabled) {
      return t('orders:payments_disabled');
    }

    return '';
  };

  const handleClick = useCallback(() => {
    const totalCartItems = cart
      ? Object.values(cart).reduce(
          (sum, assetCart) => sum + (assetCart?.items?.length ?? 0),
          0,
        )
      : 0;

    trackMixpanel({
      event: EventTypes.CHECKOUT_ORDER_CLICKED,
      properties: {
        placement,
        checkout_context_state: getCheckoutContextState(totalCartItems),
      },
    });

    gtmService.sendEvent({
      event: AnalyticsEventTypes.NewAccountClick,
      params: {
        button_text: ButtonText.NewAccount,
      },
    });

    navigate(PagePath.Purchase);
  }, [navigate, placement, cart]);

  return (
    <ThemeWrapper themeColor={themeColor}>
      <Tooltip title={getTooltipText()}>
        <Button
          disabled={shouldDisableButton}
          onClick={handleClick}
          type="primary"
          id={id}
          data-testid={dataTestId}
          {...rest}
        >
          {children}
        </Button>
      </Tooltip>
    </ThemeWrapper>
  );
};

export default VerificationRequiredButton;
